import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Refresh,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { widgetTheme } from '../../theme/darkTheme';
import { mockDriftAnalysisData } from '../../services/mockData';
import { apiService } from '../../services/api';

interface DriftAnalysisWidgetProps {
  widgetId: string;
  title: string;
  data?: any[];
  onRefresh?: () => void;
  onConfigure?: () => void;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
}

// Helper to parse date strings without timezone issues
const parseDateString = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  // Append T12:00:00 to avoid timezone boundary issues
  if (!dateStr.includes('T')) {
    return new Date(dateStr + 'T12:00:00');
  }
  const date = new Date(dateStr);
  date.setHours(12, 0, 0, 0);
  return date;
};

const DriftAnalysisWidget: React.FC<DriftAnalysisWidgetProps> = ({
  widgetId,
  title,
  data = [],
  onRefresh,
  onConfigure,
  isVisible = true,
  onToggleVisibility,
}) => {
  const [driftData, setDriftData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetchDriftData();
  }, []);

  const fetchDriftData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDriftAnalysisData();
      if (response.success && response.data && Array.isArray(response.data)) {
        setDriftData(response.data);
      } else {
        const errorMsg = response.error || 'Failed to load drift analysis data';
        setError(errorMsg);
        console.error('Drift analysis API error:', errorMsg);
        // Only use mock data if explicitly requested (for development)
        if (process.env.NODE_ENV === 'development') {
          setDriftData(mockDriftAnalysisData);
        }
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error fetching drift data';
      setError(errorMsg);
      console.error('Error fetching drift data:', error);
      // Only use mock data in development
      if (process.env.NODE_ENV === 'development') {
        setDriftData(mockDriftAnalysisData);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await fetchDriftData();
    onRefresh?.();
  };

  const currentData = Array.isArray(data) && data.length > 0 ? data : (Array.isArray(driftData) ? driftData : []);

  const getStatusColor = (status: string | null | undefined) => {
    if (!status) return widgetTheme.colors.status.onTrack;
    switch (status) {
      case 'ahead': return widgetTheme.colors.status.ahead;
      case 'on_track': return widgetTheme.colors.status.onTrack;
      case 'behind': return widgetTheme.colors.status.behind;
      case 'critical': return widgetTheme.colors.status.critical;
      default: return widgetTheme.colors.status.onTrack;
    }
  };

  // const getStatusIcon = (status: string) => {
  //   switch (status) {
  //     case 'ahead': return <TrendingUp />;
  //     case 'behind': return <TrendingDown />;
  //     case 'critical': return <Stop />;
  //     default: return <Schedule />;
  //   }
  // };

  const getStatusLabel = (status: string | null | undefined) => {
    if (!status) return 'N/A';
    return status.replace('_', ' ').toUpperCase();
  };

  // Find the last day that has actual data (pages shot > 0)
  const getLastDayWithActuals = () => {
    if (!currentData || currentData.length === 0) return null;
    // Search from end to find most recent day with actual pages
    for (let i = currentData.length - 1; i >= 0; i--) {
      const day = currentData[i];
      const actualPages = day.actual_shot_pages_decimal || parseFloat(day.actual_shot_pages) || 0;
      if (actualPages > 0) {
        return day;
      }
    }
    return null;
  };

  const lastDayWithActuals = getLastDayWithActuals();
  
  // Get cumulative drift value from the last day with actuals (check multiple field names)
  const getCumulativeDrift = () => {
    if (!lastDayWithActuals) return { display: '—', numeric: 0 };
    // Check fields in priority order: revised first (most reliable), then since_latest_revision, then base
    // Session 16 fix: Reordered to check cumulative_drift_revised first since 
    // cumulative_since_latest_revision may not exist in SQL function and defaults to "0"
    const fields = [
      { display: 'cumulative_drift_revised', numeric: 'cumulative_drift_revised_numeric' },
      { display: 'cumulative_since_latest_revision', numeric: 'cumulative_since_latest_revision_numeric' },
      { display: 'cumulative_drift', numeric: 'cumulative_drift_numeric' },
    ];
    for (const field of fields) {
      const displayVal = lastDayWithActuals[field.display];
      const numericVal = lastDayWithActuals[field.numeric];
      // Only use this field if it has a meaningful value (not undefined/null)
      // AND (has a non-zero numeric value OR is the cumulative_drift_revised field which is always valid)
      if (displayVal !== undefined && displayVal !== null && 
          (numericVal !== 0 || field.display === 'cumulative_drift_revised')) {
        return { display: displayVal, numeric: numericVal || 0 };
      }
    }
    // Fallback only if no cumulative fields exist at all
    return { 
      display: '0', 
      numeric: 0 
    };
  };

  const cumulativeDrift = getCumulativeDrift();

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader title={title} />
        <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading drift analysis data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: '100%', maxHeight: 'none', overflow: 'hidden' }}>
      <CardHeader
        title={title}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRefresh} size="small" disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure Widget">
              <IconButton onClick={onConfigure} size="small">
                <Settings />
              </IconButton>
            </Tooltip>
            <Tooltip title={isVisible ? "Hide Widget" : "Show Widget"}>
              <IconButton onClick={onToggleVisibility} size="small">
                {isVisible ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      
      <CardContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {/* Error Display */}
        {error && (
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}
        
        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} flexGrow={1}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Summary Metrics */}
        {!loading && !error && (
          <Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
            {/* Cumulative/Daily Metrics */}
            <Box display="flex" gap={2}>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                <Typography 
                  variant="h6" 
                  color={cumulativeDrift.numeric >= 0 ? 'success.main' : 'error.main'}
                >
                  {cumulativeDrift.display}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cumulative Drift
                </Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                <Typography 
                  variant="h6" 
                  color={(lastDayWithActuals?.daily_variance_decimal || 0) >= 0 ? 'success.main' : 'error.main'}
                >
                  {lastDayWithActuals?.daily_variance || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Daily Variance
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}

        {/* Drift Table */}
        {!loading && !error && (
          <Box sx={{ flexGrow: 1, p: 2, pt: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <TableContainer 
            component={Paper} 
            sx={{ 
              flex: 1,
              overflow: 'scroll',
              '& .MuiTableHead-root': {
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                zIndex: 1
              }
            }}
          >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Actual</TableCell>
                <TableCell>Daily</TableCell>
                <TableCell>Since Revision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentData.map((day) => (
                <TableRow key={day.shoot_day_number}>
                  <TableCell>{day.shoot_day_number}</TableCell>
                  <TableCell>{parseDateString(day.shoot_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={day.current_plan_name || day.schedule_version} 
                      size="small" 
                      color="primary"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                      title={day.schedule_version} // Show full filename on hover
                    />
                  </TableCell>
                  <TableCell>{day.revised_planned_pages || '0'}</TableCell>
                  <TableCell>{day.actual_shot_pages || '0'}</TableCell>
                  <TableCell>
                    <Typography 
                      color={(day.daily_variance_numeric || 0) >= 0 ? 'success' : 'error'}
                      variant="body2"
                    >
                      {day.daily_variance || '0'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      color={(day.cumulative_since_latest_revision_numeric || 0) >= 0 ? 'success' : 'error'}
                      variant="body2"
                    >
                      {day.cumulative_since_latest_revision || '0'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
        )}
      </CardContent>

    </Card>
  );
};

export default DriftAnalysisWidget;
